import React, { useState, useEffect } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Save, MessageSquare, ExternalLink, CheckCircle2 } from "lucide-react";

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({});
  const [smsTestPhone, setSmsTestPhone] = useState("");
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [smsResult, setSmsResult] = useState(null);

  useEffect(() => {
    if (user) {
      setForm({
        full_name: user.full_name || "",
        profession: user.profession || "",
        siret: user.siret || "",
        ape_code: user.ape_code || "",
        adeli_number: user.adeli_number || "",
        vat_mention_article: user.vat_mention_article || "293B",
        dispense_rcs_rm: !!user.dispense_rcs_rm,
        mediator_info: user.mediator_info || "",
        address: user.address || "",
        phone: user.phone || "",
        iban: user.iban || "",
        urssaf_regime: user.urssaf_regime || "SSI",
        google_calendar_url: user.google_calendar_url || "",
        default_session_price: user.default_session_price || "",
        booking_enabled: !!user.booking_enabled,
        booking_slug: user.booking_slug || "",
        booking_slot_minutes: user.booking_slot_minutes || 60,
        working_hours: (() => {
          // Convert backend arrays → editable strings for the UI
          const src = user.working_hours || {
            mon: ["09:00-12:00","14:00-18:00"], tue: ["09:00-12:00","14:00-18:00"],
            wed: ["09:00-12:00","14:00-18:00"], thu: ["09:00-12:00","14:00-18:00"],
            fri: ["09:00-12:00","14:00-18:00"], sat: [], sun: [],
          };
          const out = {};
          for (const [k, v] of Object.entries(src)) {
            out[k] = Array.isArray(v) ? v.join(",") : (v || "");
          }
          return out;
        })(),
        sms_enabled: !!user.sms_enabled,
        sms_account_sid: user.sms_account_sid || "",
        sms_auth_token: "",  // never sent back from server
        sms_from_number: user.sms_from_number || "",
        sms_sender_name: user.sms_sender_name || "",
      });
      setSmsTestPhone(user.phone || "");
    }
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setError(""); setSaved(false);
    const payload = { ...form };
    if (!payload.sms_auth_token) delete payload.sms_auth_token;
    if (payload.default_session_price === "" || payload.default_session_price === null) {
      delete payload.default_session_price;
    } else {
      payload.default_session_price = parseFloat(payload.default_session_price) || 0;
    }
    // Convert working_hours strings → arrays before sending to backend
    if (payload.working_hours && typeof payload.working_hours === "object") {
      const wh = {};
      for (const [k, v] of Object.entries(payload.working_hours)) {
        if (typeof v === "string") {
          wh[k] = v.split(",").map((s) => s.trim()).filter(Boolean);
        } else if (Array.isArray(v)) {
          wh[k] = v;
        } else {
          wh[k] = [];
        }
      }
      payload.working_hours = wh;
    }
    try {
      await api.put("/auth/profile", payload);
      await refreshUser();
      setForm((f) => ({ ...f, sms_auth_token: "" }));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
  };

  const sendTestSms = async () => {
    setSmsResult(null);
    if (!smsTestPhone) { setSmsResult({ ok: false, msg: "Renseignez un numéro de test." }); return; }
    try {
      const { data } = await api.post("/auth/sms/test", { to: smsTestPhone });
      setSmsResult({ ok: true, msg: `SMS envoyé ! (id : ${data.sid})` });
    } catch (e) {
      setSmsResult({ ok: false, msg: formatApiError(e.response?.data?.detail) });
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-container">
      <div>
        <h1 className="font-display text-3xl">Paramètres</h1>
        <p className="text-muted-soft mt-1">Vos informations personnelles et professionnelles.</p>
      </div>

      <div className="card-soft p-7 space-y-4" data-testid="settings-profile">
        <div className="font-display text-lg mb-2">Profil</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Nom complet"><input className="input-soft" value={form.full_name || ""} onChange={set("full_name")} data-testid="settings-name" /></Field>
          <Field label="Profession"><input className="input-soft" value={form.profession || ""} onChange={set("profession")} data-testid="settings-profession" /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="SIRET"><input className="input-soft" value={form.siret || ""} onChange={set("siret")} data-testid="settings-siret" /></Field>
          <Field label="Code APE (mention obligatoire sur les factures)"><input className="input-soft" placeholder="8690F" value={form.ape_code || ""} onChange={set("ape_code")} data-testid="settings-ape" /></Field>
        </div>
        <Field label="Numéro ADELI ou RPPS (si vous en avez un)">
          <input className="input-soft" placeholder="Ex : 759912345"
            value={form.adeli_number || ""} onChange={set("adeli_number")}
            data-testid="settings-adeli" />
        </Field>
        <Field label="Mention de TVA sur vos factures">
          <select className="input-soft" value={form.vat_mention_article || "293B"}
            onChange={set("vat_mention_article")} data-testid="settings-vat-article">
            <option value="293B">TVA non applicable, article 293 B du CGI</option>
            <option value="261-4-1">TVA non applicable, article 261-4-1° du CGI</option>
          </select>
          <div className="text-[11px] text-muted-soft mt-1.5 leading-relaxed">
            Vous avez un numéro ADELI/RPPS (profession de santé réglementée&nbsp;: ostéopathe,
            psychologue, psychothérapeute diplômé, chiropracteur)&nbsp;? Choisissez l'article <b>261-4-1°</b>.
            Sinon (sophrologue, naturopathe, hypnothérapeute, réflexologue, coach bien-être…),
            choisissez l'article <b>293 B</b>.
          </div>
        </Field>
        <label className="flex items-start gap-3 cursor-pointer text-sm py-1">
          <input type="checkbox" checked={!!form.dispense_rcs_rm}
            onChange={(e) => setForm({ ...form, dispense_rcs_rm: e.target.checked })}
            className="mt-1 w-4 h-4 accent-sage" data-testid="settings-dispense-rcs-rm" />
          <span>
            <b>Dispensé d'immatriculation au RCS et au RM</b><br/>
            <span className="text-[11px] text-muted-soft leading-relaxed">
              Cochez uniquement si vous êtes <b>auto-entrepreneur en activité commerciale ou artisanale</b>{" "}
              (art. L123-1-1 code de commerce). Les professions libérales pures (sophrologue, kiné,
              ostéopathe…) ne cochent pas cette case.
            </span>
          </span>
        </label>
        <Field label="Médiateur de la consommation (obligatoire B2C — art. L616-1 Code conso)">
          <textarea className="input-soft" style={{ minHeight: 70, paddingTop: 12 }}
            placeholder="Ex : CM2C · 14 rue Saint-Jean, 75017 Paris · cm2c.net · litiges@cm2c.net"
            value={form.mediator_info || ""} onChange={set("mediator_info")}
            data-testid="settings-mediator-info" />
          <div className="text-[11px] text-muted-soft mt-1 leading-relaxed">
            Coordonnées complètes (nom + adresse + email/site) affichées en bas de vos factures.
            Adhérer à un médiateur agréé est obligatoire dès lors que vous facturez des particuliers.
          </div>
        </Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Téléphone"><input className="input-soft" value={form.phone || ""} onChange={set("phone")} data-testid="settings-phone" /></Field>
          <Field label="Adresse professionnelle"><input className="input-soft" value={form.address || ""} onChange={set("address")} data-testid="settings-address" /></Field>
        </div>
        <Field label="IBAN (sera affiché sur les factures)"><input className="input-soft" value={form.iban || ""} onChange={set("iban")} data-testid="settings-iban" /></Field>
        <Field label="Régime URSSAF — important pour le calcul des charges">
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setForm({ ...form, urssaf_regime: "SSI" })} data-testid="settings-regime-ssi"
              className={`px-3 py-3 rounded-xl text-sm transition-all border text-left ${form.urssaf_regime === "SSI" ? "bg-sage text-white border-sage" : "border-border hover:bg-sage-light"}`}>
              <div className="font-medium">SSI · 25,6 %</div>
              <div className="text-xs opacity-80 mt-0.5">Professions libérales non réglementées hors Cipav</div>
            </button>
            <button type="button" onClick={() => setForm({ ...form, urssaf_regime: "CIPAV" })} data-testid="settings-regime-cipav"
              className={`px-3 py-3 rounded-xl text-sm transition-all border text-left ${form.urssaf_regime === "CIPAV" ? "bg-sage text-white border-sage" : "border-border hover:bg-sage-light"}`}>
              <div className="font-medium">Cipav · 23,2 %</div>
              <div className="text-xs opacity-80 mt-0.5">Professions relevant de la Cipav</div>
            </button>
          </div>
          <div className="text-[11px] text-muted-soft mt-1.5 leading-relaxed">
            Vérifiez votre régime sur votre attestation URSSAF avant de choisir — nous n'appliquons aucun taux par défaut.
          </div>
        </Field>
        <Field label="Tarif standard d'une séance (€) — pré-rempli sur vos factures et séances">
          <div className="relative">
            <input
              type="number" min="0" step="0.01"
              className="input-soft pr-10"
              placeholder="60"
              value={form.default_session_price || ""}
              onChange={set("default_session_price")}
              data-testid="settings-default-price"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-soft text-sm">€</span>
          </div>
        </Field>
        <Field label="Lien Google Calendar (optionnel)"><input className="input-soft" placeholder="https://calendar.google.com/..." value={form.google_calendar_url || ""} onChange={set("google_calendar_url")} data-testid="settings-gcal" /></Field>
        <Field label="🖼️ Logo du cabinet (apparaît sur vos factures)">
          <div className="flex items-center gap-3">
            {user?.logo_b64 && (
              <img src={`data:${user.logo_mime||"image/png"};base64,${user.logo_b64}`}
                   alt="Logo cabinet" className="w-14 h-14 object-contain rounded-lg border" style={{borderColor:"var(--border)"}}/>
            )}
            <label className="btn-ghost cursor-pointer text-sm">
              {user?.logo_b64 ? "Remplacer" : "Uploader"}
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                data-testid="settings-logo-upload"
                onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  const fd = new FormData(); fd.append("file", f);
                  try {
                    await api.post("/auth/profile/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    await refreshUser();
                    setSaved(true); setTimeout(()=>setSaved(false),3000);
                  } catch (err) { setError(formatApiError(err.response?.data?.detail||"Upload échoué")); }
                }} />
            </label>
            {user?.logo_b64 && (
              <button type="button" className="text-xs text-red-500 underline"
                onClick={async () => { await api.delete("/auth/profile/logo"); await refreshUser(); }}
                data-testid="settings-logo-delete">Supprimer</button>
            )}
          </div>
          <div className="text-[11px] text-muted-soft mt-1">PNG, JPEG ou WEBP · max 500 KB · idéalement carré ou format 3:2.</div>
        </Field>
      </div>

      {/* ─── Prise de RDV publique ────────────────────────────── */}
      <div className="card-soft p-7 space-y-3" data-testid="settings-booking">
        <div className="font-display text-lg mb-1">📅 Prise de RDV en ligne (page publique)</div>
        <div className="text-xs text-muted-soft mb-3">
          Activez pour que vos clients puissent réserver eux-mêmes leurs séances 24/7.
          Vous conservez la main sur l'agenda et pouvez annuler ou déplacer chaque RDV.
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={!!form.booking_enabled}
            onChange={(e) => setForm({ ...form, booking_enabled: e.target.checked })}
            className="w-4 h-4 accent-sage" data-testid="booking-enabled-toggle" />
          <span className="text-sm">Activer ma page publique de réservation</span>
        </label>
        {form.booking_enabled && (
          <>
            <Field label="Votre URL publique (utilisée par vos clients)">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-soft whitespace-nowrap">cabinet.capsulepatrimoine.com/r/</span>
                <input className="input-soft" value={form.booking_slug || ""}
                  onChange={(e) => setForm({...form, booking_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,"-")})}
                  placeholder="prenom-nom" data-testid="booking-slug"/>
              </div>
            </Field>
            <Field label="Durée d'une séance (minutes)">
              <input type="number" min="15" step="15" className="input-soft"
                value={form.booking_slot_minutes || 60}
                onChange={(e) => setForm({...form, booking_slot_minutes: parseInt(e.target.value)||60})}
                data-testid="booking-slot-minutes"/>
            </Field>
            <div className="text-xs text-muted-soft font-medium mt-2 mb-1">Horaires d'ouverture (par jour)</div>
            <div className="text-[11px] text-muted-soft mb-2">
              Format : plages séparées par une virgule. Ex : <code>09:00-12:00,14:00-18:00</code>. Laissez vide pour fermer.
            </div>
            {[["mon","Lundi"],["tue","Mardi"],["wed","Mercredi"],["thu","Jeudi"],["fri","Vendredi"],["sat","Samedi"],["sun","Dimanche"]].map(([k,label])=>(
              <div key={k} className="grid grid-cols-4 gap-2 items-center">
                <label className="text-xs text-muted-soft">{label}</label>
                <input className="input-soft col-span-3"
                  value={typeof form.working_hours === "object" ? (form.working_hours[k]||"") : ""}
                  onChange={(e) => setForm({...form, working_hours: {...(form.working_hours||{}), [k]: e.target.value}})}
                  placeholder={k === "sat" || k === "sun" ? "(fermé)" : "09:00-12:00,14:00-18:00"}
                  data-testid={`booking-hours-${k}`}/>
              </div>
            ))}
            {form.booking_slug && (
              <div className="mt-3 p-3 rounded-xl bg-sage-light text-xs text-sage">
                📎 Votre lien à partager :
                <a href={`https://cabinet.capsulepatrimoine.com/r/${form.booking_slug}`}
                   target="_blank" rel="noreferrer" className="underline ml-2">
                  cabinet.capsulepatrimoine.com/r/{form.booking_slug}
                </a>
              </div>
            )}
          </>
        )}
      </div>

      <div className="card-soft p-7" data-testid="settings-sms">
        <div className="flex items-start gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-sage-light flex items-center justify-center flex-shrink-0">
            <MessageSquare className="w-5 h-5 text-sage" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-display text-lg">Rappels SMS automatiques</div>
            <div className="text-xs text-muted-soft mt-0.5">
              Connectez votre compte Twilio pour envoyer un SMS de confirmation à la prise de RDV
              et un rappel automatique la veille à 10h. <b>Vous gardez la main sur votre forfait SMS.</b>
            </div>
          </div>
        </div>

        <div className="rounded-xl p-3 mb-4 text-xs leading-relaxed" style={{ backgroundColor: "var(--sage-light)" }}>
          <b>Comment obtenir vos identifiants Twilio</b> (gratuit avec ~15 € de crédit offert) :
          <ol className="mt-2 ml-4 list-decimal space-y-0.5">
            <li>Créez un compte sur <a href="https://www.twilio.com/try-twilio" target="_blank" rel="noreferrer" className="text-sage underline">twilio.com/try-twilio</a></li>
            <li>Récupérez vos identifiants ici : <a href="https://console.twilio.com" target="_blank" rel="noreferrer" className="text-sage underline">console.twilio.com</a> <ExternalLink className="inline w-3 h-3" /></li>
            <li>Achetez un numéro français (1 €/mois), copiez-le au format <code>+33...</code></li>
            <li>Collez les 3 valeurs ci-dessous</li>
          </ol>
        </div>

        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={!!form.sms_enabled} onChange={(e) => setForm({ ...form, sms_enabled: e.target.checked })} className="w-4 h-4 accent-sage" data-testid="sms-enabled-toggle" />
            <span className="text-sm">Activer l'envoi automatique de SMS</span>
          </label>

          <Field label="Twilio Account SID">
            <input className="input-soft" placeholder="AC..." value={form.sms_account_sid || ""} onChange={set("sms_account_sid")} data-testid="sms-account-sid" />
          </Field>
          <Field label="Twilio Auth Token (laissez vide pour conserver l'existant)">
            <input className="input-soft" type="password" placeholder={user?.sms_configured ? "•••••• (déjà configuré)" : "Votre Auth Token"} value={form.sms_auth_token || ""} onChange={set("sms_auth_token")} data-testid="sms-auth-token" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Numéro Twilio (expéditeur, format +33...)">
              <input className="input-soft" placeholder="+33756123456" value={form.sms_from_number || ""} onChange={set("sms_from_number")} data-testid="sms-from-number" />
            </Field>
            <Field label="Nom expéditeur (optionnel, max 11 car.)">
              <input className="input-soft" placeholder="Cabinet ML" maxLength={11} value={form.sms_sender_name || ""} onChange={set("sms_sender_name")} data-testid="sms-sender-name" />
            </Field>
          </div>
        </div>

        {user?.sms_configured && (
          <div className="mt-5 pt-5 border-t" style={{ borderColor: "var(--border)" }}>
            <div className="text-sm font-medium mb-2 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-sage" />
              Tester l'envoi
            </div>
            <div className="flex gap-2">
              <input className="input-soft flex-1" placeholder="0612345678" value={smsTestPhone} onChange={(e) => setSmsTestPhone(e.target.value)} data-testid="sms-test-phone" />
              <button onClick={sendTestSms} className="btn-ghost text-sm whitespace-nowrap" data-testid="sms-test-send">Envoyer un SMS test</button>
            </div>
            {smsResult && (
              <div className="mt-2 text-xs" style={{ color: smsResult.ok ? "var(--sage)" : "#8B4D3D" }} data-testid="sms-test-result">
                {smsResult.msg}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="card-soft p-7" data-testid="settings-subscription">
        <div className="font-display text-lg mb-1">Abonnement</div>
        <p className="text-sm text-muted-soft mb-4">
          Statut : <b className={user?.subscription_status === "active" ? "text-sage" : "text-clay"}>
            {user?.subscription_status === "active" ? "Actif" : "En attente"}
          </b>
          {user?.stripe_subscription_id && <span className="block text-xs mt-1">ID Stripe : {user.stripe_subscription_id}</span>}
        </p>
        <button
          onClick={async () => {
            try {
              const { data } = await api.post("/billing/portal", { return_url: window.location.origin + "/settings" });
              window.location.href = data.url;
            } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
          }}
          className="btn-ghost text-sm"
          data-testid="settings-portal"
        >
          Gérer mon abonnement (résiliation, CB, factures…)
        </button>
      </div>

      {error && <div className="text-sm text-clay">{error}</div>}
      {saved && <div className="text-sm text-sage">Modifications enregistrées.</div>}

      <div className="flex justify-end">
        <button onClick={save} className="btn-sage text-sm" data-testid="settings-save">
          <Save className="inline w-4 h-4 mr-1.5" strokeWidth={1.6} />
          Enregistrer
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-sm font-medium block mb-2">{label}</label>
      {children}
    </div>
  );
}
